import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { STAGE_LABELS, PERMISSION_STAGES } from "../lib/stages";
import { toast } from "sonner";
import { PAGE_PERMISSIONS } from "../lib/permissions";
import ResponsiveDialog from "../components/ResponsiveDialog";

const ROLES = ["admin", "staff", "inspector", "cleaner", "maintenance", "contractor", "cleaning_admin"];

export default function Users() {
  const [users, setUsers] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [sites, setSites] = useState([]);
  const [roleLabels, setRoleLabels] = useState({});
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    const [u, b, s, cfg] = await Promise.all([
      api.get("/users"),
      api.get("/blocks"),
      api.get("/sites"),
      api.get("/settings"),
    ]);
    setUsers(u.data);
    setBlocks(b.data);
    setSites(s.data || []);
    setRoleLabels(cfg.data?.role_labels || {});
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="users-page">
      <div className="flex items-end justify-between mb-6">
        <h1 className="font-chivo font-black text-4xl tracking-tight">Users</h1>
        <button onClick={() => setCreating(true)} data-testid="add-user-button" className="bg-black text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-gray-800">+ Add User</button>
      </div>

      <div className="bg-white border border-gray-200 overflow-x-auto">
        <div className="min-w-[900px]">
          <div className="grid grid-cols-[2fr_1fr_1.2fr_1.4fr_1.2fr_120px] gap-3 px-4 py-2 border-b border-gray-200 text-[10px] font-mono uppercase tracking-wider text-gray-500">
            <div>User</div><div>Role</div><div>Allowed blocks</div><div>Allowed stages</div><div>Allowed pages</div><div></div>
          </div>
          {users.map((u) => (
            <div key={u.id} className="grid grid-cols-[2fr_1fr_1.2fr_1.4fr_1.2fr_120px] gap-3 px-4 py-3 border-b border-gray-100 last:border-b-0 items-center" data-testid={`user-row-${u.email}`}>
              <div><div className="font-chivo font-bold text-sm">{u.name}</div><div className="text-[11px] font-mono text-gray-500">{u.email}</div></div>
              <div><span className="text-[10px] font-mono font-bold uppercase tracking-wider border border-gray-300 px-1.5 py-0.5">{roleLabels[u.role] || u.role}</span></div>
              <div className="text-[11px] font-mono">{u.allowed_blocks?.includes("*") ? "All blocks" : `${u.allowed_blocks?.length || 0} blocks`}</div>
              <div className="text-[11px] font-mono">{u.allowed_stages?.includes("*") ? "All stages" : (u.allowed_stages || []).map((s) => STAGE_LABELS[s] || s).join(", ")}</div>
              <div className="text-[11px] font-mono">{u.allowed_pages?.includes("*") ? "All pages" : (u.allowed_pages || []).map((p) => PAGE_PERMISSIONS.find((item) => item.id === p)?.label || p).join(", ")}</div>
              <div className="text-right"><button onClick={() => setEditing(u)} data-testid={`edit-user-${u.email}`} className="text-[10px] font-mono font-bold uppercase border border-black hover:bg-black hover:text-white px-2 py-1">Edit</button></div>
            </div>
          ))}
        </div>
      </div>

      {editing && <UserEditModal user={editing} blocks={blocks} sites={sites} roleLabels={roleLabels} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {creating && <UserCreateModal blocks={blocks} sites={sites} roleLabels={roleLabels} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
    </div>
  );
}

function UserEditModal({ user, blocks, sites, roleLabels, onClose, onSaved }) {
  const [role, setRole] = useState(user.role);
  const [allBlocks, setAllBlocks] = useState(user.allowed_blocks?.includes("*"));
  const [blockIds, setBlockIds] = useState(user.allowed_blocks?.includes("*") ? [] : user.allowed_blocks || []);
  const [allStages, setAllStages] = useState(user.allowed_stages?.includes("*"));
  const [stageList, setStageList] = useState(user.allowed_stages?.includes("*") ? [] : user.allowed_stages || []);
  const [allowKanban, setAllowKanban] = useState(!!user.allow_kanban);
  const [allPages, setAllPages] = useState(user.allowed_pages?.includes("*"));
  const [pageList, setPageList] = useState(user.allowed_pages?.includes("*") ? [] : user.allowed_pages || []);
  const [allSites, setAllSites] = useState(user.allowed_sites?.includes("*"));
  const [siteList, setSiteList] = useState(user.allowed_sites?.includes("*") ? [] : user.allowed_sites || []);

  const save = async () => {
    await api.patch(`/users/${user.id}`, { role, allowed_blocks: allBlocks ? ["*"] : blockIds, allowed_stages: allStages ? ["*"] : stageList, allowed_pages: allPages ? ["*"] : pageList, allow_kanban: allowKanban, allowed_sites: allSites ? ["*"] : siteList });
    toast.success("User updated");
    onSaved();
  };

  return (
    <Modal title={`Edit · ${user.email}`} onClose={onClose}>
      <div className="space-y-4">
        <div><Label>Role</Label><select value={role} onChange={(e) => setRole(e.target.value)} data-testid="user-role-select" className="w-full border border-gray-300 px-3 py-2 text-sm bg-white">{ROLES.map((r) => <option key={r} value={r}>{roleLabels?.[r] || r}</option>)}</select></div>
        <PermissionGroup title="Sites" all={allSites} setAll={setAllSites} options={(sites || []).map((s) => ({ id: s.id, label: s.name }))} selected={siteList} setSelected={setSiteList} testId="sites" />
        <PermissionGroup title="Blocks" all={allBlocks} setAll={setAllBlocks} options={blocks.map((b) => ({ id: b.id, label: `${b.code} · ${b.name}` }))} selected={blockIds} setSelected={setBlockIds} testId="blocks" />
        <PermissionGroup title="Stages" all={allStages} setAll={setAllStages} options={PERMISSION_STAGES.map((s) => ({ id: s, label: STAGE_LABELS[s] }))} selected={stageList} setSelected={setStageList} testId="stages" />
        <PermissionGroup title="Pages" all={allPages} setAll={setAllPages} options={PAGE_PERMISSIONS} selected={pageList} setSelected={setPageList} testId="pages" />
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!allowKanban} onChange={(e) => setAllowKanban(e.target.checked)} data-testid="user-allow-kanban" className="w-4 h-4 accent-black" />Allow Kanban access</label>
      </div>
      <div className="flex justify-between gap-2 mt-6">
        <button onClick={async () => { if (!window.confirm(`Remove ${user.email} from this organisation?`)) return; await api.delete(`/users/${user.id}`); toast.success("Membership removed"); onSaved(); }} data-testid="user-remove-button" className="px-4 py-2 text-xs font-semibold uppercase tracking-wider border border-red-300 text-red-700 hover:bg-red-50">Remove membership</button>
        <div className="flex gap-2">
          <button onClick={onClose} className="px-4 py-2 text-xs font-semibold uppercase tracking-wider border border-gray-300 hover:border-black">Cancel</button>
          <button onClick={save} data-testid="user-save-button" className="bg-black text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-gray-800">Save</button>
        </div>
      </div>
    </Modal>
  );
}

function UserCreateModal({ blocks, sites, roleLabels, onClose, onSaved }) {
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "staff" });
  const [allBlocks, setAllBlocks] = useState(true);
  const [blockIds, setBlockIds] = useState([]);
  const [allStages, setAllStages] = useState(true);
  const [stageList, setStageList] = useState([]);
  const [allSites, setAllSites] = useState(true);
  const [siteList, setSiteList] = useState([]);
  const [allPages, setAllPages] = useState(true);
  const [pageList, setPageList] = useState([]);
  const [allowKanban, setAllowKanban] = useState(false);

  return (
    <Modal title="Add user" onClose={onClose}>
      <div className="space-y-3">
        <Field testId="new-user-name" label="Name" v={form.name} on={(v) => setForm({ ...form, name: v })} autoComplete="off" inputName="new-user-name" />
        <Field testId="new-user-email" label="Email" v={form.email} on={(v) => setForm({ ...form, email: v })} autoComplete="off" inputName="new-user-email" />
        <Field testId="new-user-password" label="Password" v={form.password} on={(v) => setForm({ ...form, password: v })} type="password" autoComplete="new-password" inputName="new-user-password" />
        <div><Label>Role</Label><select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="new-user-role" className="w-full border border-gray-300 px-3 py-2 text-sm bg-white">{ROLES.map((r) => <option key={r} value={r}>{roleLabels?.[r] || r}</option>)}</select></div>
        <PermissionGroup title="Sites" all={allSites} setAll={setAllSites} options={(sites || []).map((s) => ({ id: s.id, label: s.name }))} selected={siteList} setSelected={setSiteList} testId="sites" />
        <PermissionGroup title="Blocks" all={allBlocks} setAll={setAllBlocks} options={blocks.map((b) => ({ id: b.id, label: `${b.code} · ${b.name}` }))} selected={blockIds} setSelected={setBlockIds} testId="new-blocks" />
        <PermissionGroup title="Stages" all={allStages} setAll={setAllStages} options={PERMISSION_STAGES.map((s) => ({ id: s, label: STAGE_LABELS[s] }))} selected={stageList} setSelected={setStageList} testId="new-stages" />
        <PermissionGroup title="Pages" all={allPages} setAll={setAllPages} options={PAGE_PERMISSIONS} selected={pageList} setSelected={setPageList} testId="new-pages" />
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={allowKanban} onChange={(e) => setAllowKanban(e.target.checked)} data-testid="new-allow-kanban" className="w-4 h-4 accent-black" />Allow Kanban access</label>
      </div>
      <div className="flex justify-end gap-2 mt-6">
        <button onClick={onClose} className="px-4 py-2 text-xs font-semibold uppercase tracking-wider border border-gray-300 hover:border-black">Cancel</button>
        <button onClick={async () => {
          try {
            await api.post("/users", { ...form, allowed_blocks: allBlocks ? ["*"] : blockIds, allowed_stages: allStages ? ["*"] : stageList, allowed_pages: allPages ? ["*"] : pageList, allowed_sites: allSites ? ["*"] : siteList, allow_kanban: allowKanban });
            toast.success("User created");
            onSaved();
          } catch (e) { toast.error(e.response?.data?.detail || "Could not create"); }
        }} data-testid="new-user-create-button" className="bg-black text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-gray-800">Create</button>
      </div>
    </Modal>
  );
}

function PermissionGroup({ title, all, setAll, options, selected, setSelected, testId }) {
  const toggle = (id) => setSelected(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  return (
    <div className="border border-gray-200 p-3">
      <div className="flex items-center justify-between mb-2">
        <Label>{title}</Label>
        <label className="flex items-center gap-2 text-xs"><input type="checkbox" className="w-4 h-4 accent-black" checked={all} data-testid={`perm-${testId}-all`} onChange={(e) => setAll(e.target.checked)} />All</label>
      </div>
      {!all && <div className="grid grid-cols-2 gap-1">{options.map((o) => <label key={o.id} className="flex items-center gap-2 text-xs"><input type="checkbox" className="w-4 h-4 accent-black" checked={selected.includes(o.id)} onChange={() => toggle(o.id)} data-testid={`perm-${testId}-${o.id}`} />{o.label}</label>)}</div>}
    </div>
  );
}

function Modal({ title, children, onClose }) {
  return <ResponsiveDialog title={title} onClose={onClose} className="sm:max-w-xl"><div className="p-5">{children}</div></ResponsiveDialog>;
}

function Label({ children }) {
  return <div className="text-xs font-mono uppercase tracking-wider text-gray-500 mb-1">{children}</div>;
}

function Field({ label, v, on, type = "text", testId, autoComplete = "on", inputName }) {
  return (
    <div>
      <Label>{label}</Label>
      <input type={type} name={inputName || testId || label} autoComplete={autoComplete} value={v} onChange={(e) => on(e.target.value)} data-testid={testId} className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-black" />
    </div>
  );
}
